import { useCallback, useEffect, useMemo, useState } from "react";
import { DashboardRenderer } from "./DashboardRenderer";
import { VncPane } from "./VncPane";
import { currentAccountId, downloadArtifact, runApi } from "../lib/runApi";
import { useRunEvents } from "../lib/useRunEvents";
import type {
  ApprovalCard,
  ArtifactRecord,
  DashboardBindings,
  PostRunProposal,
  PreflightPlan,
  RunIntent,
  RunLogEntry,
  RunStepRecord,
  Task,
  TaskRun,
  WorkflowVariable,
} from "../lib/runTypes";

function statusClass(status: string): string {
  switch (status) {
    case "running":
    case "queued":
      return "tj-badge tj-badge-amber";
    case "error":
    case "cancelled":
      return "tj-badge tj-badge-red";
    case "complete":
      return "tj-badge tj-badge-green";
    default:
      return "tj-badge";
  }
}

function stepClass(status: RunStepRecord["status"]): string {
  if (status === "complete") return "tj-bubble-green";
  if (status === "failed") return "tj-bubble-red";
  return "tj-bubble-amber";
}

export function RunConsole({ taskId }: { taskId: string }) {
  const accountId = currentAccountId();
  const [task, setTask] = useState<Task | null>(null);
  const [runs, setRuns] = useState<TaskRun[]>([]);
  const [run, setRun] = useState<TaskRun | null>(null);
  const [steps, setSteps] = useState<RunStepRecord[]>([]);
  const [logs, setLogs] = useState<RunLogEntry[]>([]);
  const [bindings, setBindings] = useState<DashboardBindings | null>(null);
  const [artifacts, setArtifacts] = useState<ArtifactRecord[]>([]);
  const [proposals, setProposals] = useState<PostRunProposal[]>([]);
  const [feedbackText, setFeedbackText] = useState("");
  const [guidance, setGuidance] = useState<Record<string, string>>({});
  const [inputs, setInputs] = useState("{}");
  const [variables, setVariables] = useState<WorkflowVariable[]>([]);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(null);
  const [preflightPlan, setPreflightPlan] = useState<PreflightPlan | null>(null);
  const [intent, setIntent] = useState<RunIntent>("run");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadTask = useCallback(async () => {
    try {
      const [loadedTask, loadedRuns] = await Promise.all([
        runApi.getTask(accountId, taskId),
        runApi.listRuns(accountId, taskId),
      ]);
      setTask(loadedTask);
      setRuns(loadedRuns);
      try {
        const detail = await runApi.getTemplateDetail(accountId, loadedTask.template_id);
        const declared = detail.current_version?.variables ?? [];
        setVariables(declared.filter((variable) => variable.source === "input"));
        setCurrentVersionId(detail.current_version?.version_id ?? null);
        setInputValues((previous) => {
          const next: Record<string, string> = {};
          for (const variable of declared.filter((item) => item.source === "input")) {
            const existing = previous[variable.name];
            const fromTask = loadedTask.inputs[variable.name];
            next[variable.name] =
              existing ??
              (fromTask === undefined || fromTask === null
                ? ""
                : typeof fromTask === "string"
                  ? fromTask
                  : JSON.stringify(fromTask));
          }
          return next;
        });
      } catch {
        // Template lookup is best-effort; the JSON input fallback still works.
      }
      setRun((current) => {
        if (current && loadedRuns.some((candidate) => candidate.run_id === current.run_id)) {
          return current;
        }
        return loadedRuns[0] ?? null;
      });
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }, [accountId, taskId]);

  useEffect(() => {
    void loadTask();
  }, [loadTask]);

  const terminal = run ? ["complete", "error", "cancelled"].includes(run.status) : false;
  const events = useRunEvents(accountId, run?.run_id ?? null, terminal);

  // Any state-changing event refreshes the cold-load snapshots.
  useEffect(() => {
    const last = events[events.length - 1];
    if (
      last &&
      ["run.finished", "run.state", "step.finished", "output.updated", "verification.result", "hitl.resolved"].includes(
        last.kind,
      )
    ) {
      setRefreshKey((value) => value + 1);
    }
  }, [events]);

  useEffect(() => {
    if (!run) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [fresh, loadedSteps, loadedLogs, loadedBindings, loadedArtifacts, loadedFeedback] =
          await Promise.all([
            runApi.getRun(accountId, run.run_id),
            runApi.steps(accountId, run.run_id),
            runApi.logs(accountId, run.run_id),
            runApi.bindings(accountId, run.run_id),
            runApi.artifacts(accountId, run.run_id),
            runApi.getFeedback(accountId, run.run_id),
          ]);
        if (cancelled) return;
        setRun(fresh);
        setSteps(loadedSteps);
        setLogs(loadedLogs);
        setBindings(loadedBindings);
        setArtifacts(loadedArtifacts);
        setFeedbackText((previous) => previous || loadedFeedback?.feedback || "");
        if (["complete", "error", "cancelled"].includes(fresh.status)) {
          const loadedProposals = await runApi.proposals(accountId);
          if (!cancelled) {
            setProposals(loadedProposals.filter((proposal) => proposal.run_id === fresh.run_id));
          }
        }
      } catch (cause) {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause));
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [accountId, run?.run_id, refreshKey]);

  const divergenceEvent = useMemo(() => {
    for (let index = events.length - 1; index >= 0; index -= 1) {
      if (events[index].kind === "divergence") return events[index];
    }
    return null;
  }, [events]);

  const quotaWarning = useMemo(
    () =>
      events.some(
        (event) =>
          event.kind === "log" &&
          ["artifact_quota_exceeded", "artifact_write_failed"].includes(
            String(event.payload.code ?? ""),
          ),
      ),
    [events],
  );

  const pendingApprovals = useMemo<ApprovalCard[]>(() => {
    const resolved = new Set(
      events
        .filter((event) => event.kind === "hitl.resolved")
        .map((event) => String(event.payload.approval_id ?? "")),
    );
    return events
      .filter((event) => event.kind === "hitl.requested")
      .filter((event) => !resolved.has(String(event.payload.approval_id ?? "")))
      .map((event) => ({
        approval_id: String(event.payload.approval_id ?? ""),
        step_id: String(event.payload.step_id ?? ""),
        action_class: String(event.payload.action_class ?? event.payload.reason ?? "action"),
        reason: String(event.payload.reason ?? ""),
        deadline: String(event.payload.deadline ?? ""),
      }))
      .filter((card) => card.approval_id);
  }, [events]);

  const act = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      setNotice(success);
      await loadTask();
      setRefreshKey((value) => value + 1);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  const coerceInputs = (): Record<string, unknown> => {
    if (variables.length === 0) {
      try {
        return inputs.trim() ? (JSON.parse(inputs) as Record<string, unknown>) : {};
      } catch {
        throw new Error("Inputs must be valid JSON");
      }
    }
    const values: Record<string, unknown> = {};
    for (const variable of variables) {
      const raw = inputValues[variable.name] ?? "";
      if (raw === "") continue;
      if (variable.ty.type === "number") values[variable.name] = Number(raw);
      else if (variable.ty.type === "bool") values[variable.name] = raw === "true";
      else values[variable.name] = raw;
    }
    return values;
  };

  const runPreflight = () =>
    act(async () => {
      if (!task || !currentVersionId) throw new Error("Template version is not available");
      const plan = await runApi.preflight({
        accountId,
        templateId: task.template_id,
        versionId: currentVersionId,
        intent,
        inputs: coerceInputs(),
      });
      setPreflightPlan(plan);
    }, "Pre-flight complete.");

  const launch = () =>
    act(async () => {
      const parsed = coerceInputs();
      if (preflightPlan?.verdict === "blocked") {
        throw new Error("Pre-flight is blocked; fix the missing prerequisites first");
      }
      const created = await runApi.launch(accountId, taskId, {
        run_intent: intent,
        inputs: parsed,
        preflight_id: preflightPlan?.run_id,
      });
      setRun(created);
      setRefreshKey((value) => value + 1);
    }, "Run launched.");

  if (error && !task) {
    return (
      <main className="tj-container py-8">
        <p className="tj-badge tj-badge-red">{error}</p>
      </main>
    );
  }

  return (
    <main className="tj-container space-y-5 py-8" data-testid="run-console">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="serif-heading text-2xl">{task?.name ?? "Task"}</h1>
          <p className="text-xs text-[var(--tj-muted)]">
            {task?.mode === "scheduled" && task.schedule
              ? `Scheduled ${task.schedule.cron_expr} (${task.schedule.timezone})`
              : "Manual task"}
            {run ? ` · version ${run.template_version_id.slice(0, 8)}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {run ? <span className={statusClass(run.status)}>{run.status}</span> : null}
          {run ? <span className="tj-badge">{run.phase.replace(/_/g, " ")}</span> : null}
          {run?.review_status ? (
            <span className="tj-badge">review: {run.review_status}</span>
          ) : null}
          {task ? (
            <button
              className="tj-btn tj-btn-secondary"
              disabled={busy}
              onClick={() =>
                act(
                  () => runApi.setTaskEnabled(accountId, taskId, task.status === "disabled"),
                  task.status === "disabled" ? "Task enabled." : "Task disabled.",
                )
              }
            >
              {task.status === "disabled" ? "Enable task" : "Disable task"}
            </button>
          ) : null}
        </div>
      </header>

      {error ? <p className="tj-badge tj-badge-red">{error}</p> : null}
      {notice ? <p className="tj-badge tj-badge-green">{notice}</p> : null}

      <section className="tj-card space-y-3" aria-label="Launch run">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs">
            Intent
            <select
              className="tj-select ml-2"
              value={intent}
              onChange={(event) => {
                setIntent(event.target.value as RunIntent);
                setPreflightPlan(null);
              }}
            >
              <option value="run">run</option>
              <option value="discover">discover (exploratory)</option>
            </select>
          </label>
          <button
            className="tj-btn tj-btn-secondary"
            disabled={busy || !currentVersionId}
            onClick={() => void runPreflight()}
          >
            Run pre-flight
          </button>
          <button
            className="tj-btn tj-btn-primary"
            disabled={busy || preflightPlan?.verdict === "blocked"}
            onClick={() => void launch()}
          >
            Launch
          </button>
        </div>
        {variables.length > 0 ? (
          <div className="tj-grid-2">
            {variables.map((variable) => (
              <label key={variable.name} className="block text-xs">
                {variable.name}
                {variable.ty.type === "enum" && variable.ty.values ? (
                  <select
                    className="tj-select mt-1 w-full"
                    value={inputValues[variable.name] ?? ""}
                    onChange={(event) => {
                      setInputValues((previous) => ({
                        ...previous,
                        [variable.name]: event.target.value,
                      }));
                      setPreflightPlan(null);
                    }}
                  >
                    <option value="">Select…</option>
                    {(variable.ty.values ?? []).map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="tj-input mt-1 w-full"
                    type={variable.ty.type === "number" ? "number" : "text"}
                    value={inputValues[variable.name] ?? ""}
                    onChange={(event) => {
                      setInputValues((previous) => ({
                        ...previous,
                        [variable.name]: event.target.value,
                      }));
                      setPreflightPlan(null);
                    }}
                  />
                )}
              </label>
            ))}
          </div>
        ) : (
          <label className="block text-xs">
            Inputs (JSON)
            <textarea
              className="tj-input mt-1 h-24 w-full font-mono"
              value={inputs}
              onChange={(event) => {
                setInputs(event.target.value);
                setPreflightPlan(null);
              }}
            />
          </label>
        )}
        {preflightPlan ? (
          <div className="space-y-1 text-xs" aria-label="Pre-flight panel">
            <span
              className={
                preflightPlan.verdict === "blocked"
                  ? "tj-badge tj-badge-red"
                  : preflightPlan.verdict === "complete"
                    ? "tj-badge tj-badge-green"
                    : "tj-badge tj-badge-amber"
              }
            >
              {preflightPlan.verdict.replace("_", " ")}
            </span>
            {preflightPlan.missing_prereqs.map((item) => (
              <p key={item} className="text-[var(--tj-muted)]">
                Blocked: {item}
              </p>
            ))}
            {preflightPlan.divergence_points.map((point) => (
              <p key={`${point.step_id}-${point.reason}`} className="text-[var(--tj-muted)]">
                Divergence at {point.step_id.slice(0, 8)}: {point.reason}
              </p>
            ))}
            {preflightPlan.narrative ? (
              <p className="text-[var(--tj-muted)]">{preflightPlan.narrative}</p>
            ) : null}
          </div>
        ) : null}
      </section>

      {run ? (
        <>
          <section className="flex flex-wrap items-center gap-3" aria-label="Run controls">
            <span className="tj-badge">
              {run.usage.steps} steps · {Math.round(run.usage.active_ms / 1000)}s ·{" "}
              {run.usage.tokens.toLocaleString()} tok · ${run.usage.cost_usd.toFixed(3)}
            </span>
            <span className="tj-badge">
              verify {run.verification.passed} pass · {run.verification.failed} fail ·{" "}
              {run.verification.not_applicable} n/a
            </span>
            {!terminal ? (
              <button
                className="tj-btn tj-btn-secondary"
                disabled={busy}
                onClick={() => act(() => runApi.cancel(accountId, run.run_id), "Cancellation requested.")}
              >
                Cancel
              </button>
            ) : (
              <>
                <button
                  className="tj-btn tj-btn-primary"
                  disabled={busy}
                  onClick={() => act(() => runApi.rerun(accountId, run.run_id), "Re-run launched.")}
                >
                  Re-run
                </button>
                <button
                  className="tj-btn tj-btn-secondary"
                  disabled={busy || run.review_status === "running"}
                  onClick={() =>
                    act(
                      () => runApi.requestReview(accountId, run.run_id),
                      "Review queued.",
                    )
                  }
                >
                  Review this run
                </button>
              </>
            )}
          </section>

          {pendingApprovals.map((card) => (
            <section key={card.approval_id} className="tj-card space-y-2" aria-label="Approval card">
              <p className="text-sm font-medium">
                Approval needed: {card.action_class}
              </p>
              <p className="text-xs text-[var(--tj-muted)]">{card.reason}</p>
              <input
                className="tj-input w-full"
                placeholder="Optional guidance for a loop extension"
                value={guidance[card.approval_id] ?? ""}
                onChange={(event) =>
                  setGuidance((previous) => ({
                    ...previous,
                    [card.approval_id]: event.target.value,
                  }))
                }
              />
              <div className="flex flex-wrap gap-2">
                <button
                  className="tj-btn tj-btn-primary"
                  disabled={busy}
                  onClick={() =>
                    act(
                      () =>
                        runApi.resolveApproval(accountId, run.run_id, card.approval_id, "approved"),
                      "Approved.",
                    )
                  }
                >
                  Approve
                </button>
                <button
                  className="tj-btn tj-btn-secondary"
                  disabled={busy}
                  onClick={() =>
                    act(
                      () =>
                        runApi.resolveApproval(accountId, run.run_id, card.approval_id, "denied"),
                      "Denied.",
                    )
                  }
                >
                  Deny
                </button>
                <button
                  className="tj-btn tj-btn-secondary"
                  disabled={busy || !(guidance[card.approval_id] ?? "").trim()}
                  onClick={() =>
                    act(
                      () =>
                        runApi.resolveApproval(
                          accountId,
                          run.run_id,
                          card.approval_id,
                          "guided",
                          undefined,
                          guidance[card.approval_id],
                        ),
                      "Guidance sent.",
                    )
                  }
                >
                  Guide
                </button>
              </div>
            </section>
          ))}

          <VncPane taskId={taskId} />

          {quotaWarning ? (
            <section className="tj-card" aria-label="Artifact quota warning">
              <p className="text-sm">
                An artifact was not stored because the run/tenant artifact quota was exceeded.
                Download the file from the portal directly or raise the tenant quota.
              </p>
            </section>
          ) : null}

          {divergenceEvent ? (
            <section className="tj-card border-[var(--badge-amber-text)]" aria-label="Divergence banner">
              <p className="text-sm">
                Unmodelled branch at{" "}
                <span className="font-mono">
                  {String(divergenceEvent.payload.step_id ?? "").slice(0, 8)}
                </span>{" "}
                ({String(divergenceEvent.payload.mode ?? "unknown")} →{" "}
                {String(divergenceEvent.payload.case_id ?? "default")}). This run deviated from
                the authored path; the post-run review can propose a branch case.
              </p>
            </section>
          ) : null}

          <section className="tj-card" aria-label="Steps">
            <h2 className="serif-heading mb-2 text-lg">Steps</h2>
            {steps.length === 0 ? (
              <p className="text-sm text-[var(--tj-muted)]">No steps recorded yet.</p>
            ) : (
              <ol className="space-y-1 text-sm">
                {steps.map((step) => (
                  <li key={`${step.step_index}-${step.step_id}`} className="flex gap-2">
                    <span className={stepClass(step.status)}>●</span>
                    <span className="font-mono text-xs">{step.step_index}</span>
                    <span>{step.kind}</span>
                    {step.outcome ? (
                      <span className="text-[var(--tj-muted)]">— {step.outcome}</span>
                    ) : null}
                  </li>
                ))}
              </ol>
            )}
          </section>

          {bindings ? (
            <section aria-label="Run dashboard">
              <DashboardRenderer
                schema={bindings.schema}
                outputs={bindings.values}
                verificationResults={bindings.verification}
              />
            </section>
          ) : null}

          <section className="tj-card" aria-label="Artifacts">
            <h2 className="serif-heading mb-2 text-lg">Artifacts</h2>
            {artifacts.length === 0 ? (
              <p className="text-sm text-[var(--tj-muted)]">No artifacts yet.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {artifacts.map((artifact) => (
                  <li key={artifact.artifact_id}>
                    <button
                      className="underline"
                      onClick={() =>
                        void downloadArtifact(accountId, artifact.artifact_id, artifact.filename).catch(
                          (cause) => setError(cause instanceof Error ? cause.message : String(cause)),
                        )
                      }
                    >
                      {artifact.filename}
                    </button>{" "}
                    <span className="text-xs text-[var(--tj-muted)]">
                      {Math.round(artifact.size_bytes / 1024)} KB
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="tj-card space-y-2" aria-label="Run feedback">
            <h2 className="serif-heading text-lg">Feedback for the reviewer</h2>
            <p className="text-xs text-[var(--tj-muted)]">
              Optional and non-blocking. Stored redacted, treated as semi-trusted; it can never
              widen an allowlist or assert a verification result.
            </p>
            <textarea
              className="tj-input h-20 w-full"
              value={feedbackText}
              onChange={(event) => setFeedbackText(event.target.value)}
              placeholder="What should the post-run review know?"
            />
            <button
              className="tj-btn tj-btn-secondary"
              disabled={busy || !feedbackText.trim()}
              onClick={() =>
                act(
                  () => runApi.setFeedback(accountId, run.run_id, feedbackText.trim()),
                  "Feedback saved.",
                )
              }
            >
              Save feedback
            </button>
          </section>

          <section className="tj-card" aria-label="Logs">
            <h2 className="serif-heading mb-2 text-lg">Logs</h2>
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap font-mono text-xs">
              {logs.length === 0
                ? "No log lines yet."
                : logs
                    .slice(-40)
                    .map(
                      (entry) =>
                        `${entry.level} ${entry.event_kind ?? ""} ${entry.redacted_message}`,
                    )
                    .join("\n")}
            </pre>
          </section>

          {proposals.length > 0 ? (
            <section className="tj-card space-y-3" aria-label="Post-run proposals">
              <h2 className="serif-heading text-lg">Post-run proposals</h2>
              {proposals.map((proposal) => (
                <div key={proposal.proposal_id} className="space-y-1 border-t border-[var(--tj-border)] pt-2">
                  <p className="text-sm">
                    <span className="tj-badge">{proposal.kind}</span> {proposal.summary}
                  </p>
                  <p className="text-xs text-[var(--tj-muted)]">
                    evidence: {proposal.observed_runs} runs · {proposal.divergence_count} divergences ·
                    status {proposal.status}
                  </p>
                  {proposal.status === "pending" ? (
                    <div className="flex gap-2">
                      <button
                        className="tj-btn tj-btn-primary"
                        disabled={busy}
                        onClick={() =>
                          act(
                            () => runApi.applyProposal(accountId, proposal.proposal_id),
                            "Proposal applied as a new version.",
                          )
                        }
                      >
                        Apply
                      </button>
                      <button
                        className="tj-btn tj-btn-secondary"
                        disabled={busy}
                        onClick={() =>
                          act(
                            () => runApi.rejectProposal(accountId, proposal.proposal_id, "rejected in console"),
                            "Proposal rejected.",
                          )
                        }
                      >
                        Reject
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </section>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-[var(--tj-muted)]">
          This task has no runs yet. Launch one above.
        </p>
      )}

      {runs.length > 1 ? (
        <section className="tj-card" aria-label="Run history">
          <h2 className="serif-heading mb-2 text-lg">Run history</h2>
          <ol className="space-y-1 text-sm">
            {runs.map((candidate) => (
              <li key={candidate.run_id}>
                <button
                  className="underline"
                  onClick={() => {
                    setRun(candidate);
                    setRefreshKey((value) => value + 1);
                  }}
                >
                  {candidate.started_at} · {candidate.status} · {candidate.error?.code ?? "ok"}
                </button>
              </li>
            ))}
          </ol>
        </section>
      ) : null}
    </main>
  );
}
